import _, {isArray} from 'lodash';
import {Middleware} from 'redux';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {isDevelopment} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {
  annotateTaskStep,
  TaskSourcesEnum,
} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {inAnimationFrameAsync} from '../../../src/helpers/async/inAnimationFrame';
import {delayAsync} from '../../../src/helpers/asyncHelpers';
import {AppAction} from '../../../src/store/appActions';
import {AppState} from '../../../src/store/appState';
import {DataGroupsActionsEnum, DataUpdatedAction} from '../../../src/store/data/dataGroupsActions';
import {
  getHasPerformanceDebugLogging,
  getHasPerformanceMetricLogging,
} from '../../../src/store/featureFlags/featureFlagsSelectors';
import {PanelActionsEnum, SetPanelAction} from '../../../src/store/panels/panelsActions';
import {fromFocusEvent} from '../../rx/focus';
import {fromVisibilityEvent} from '../../rx/visibility';
import {reportToBugsnag} from '../bugsnag/bugsnag';
import {interactionPerformanceLogger} from './interactionPerformance';
import {shouldDiscardInteraction, windowListenerInitialValue} from './reduxPerformanceHelpers';

/*
 * State.
 */

const shouldSampleAllEventsInDev = false;
const sampleRate = 0.01; // Sample 1% of Redux actions for users who have performance logging enabled.

let isWindowListenerSetup = false;
let lastVisibilityChange = windowListenerInitialValue;
let lastFocusChange = windowListenerInitialValue;

/*
 * Middleware.
 */

export const reduxTimingMiddleware: Middleware =
  ({getState}) =>
  (next) =>
  (action: AppAction) => {
    try {
      annotateAction(action);
      measureReduxRender(action, getState());
    } catch (error) {
      reportToBugsnag(new ExtendedError('Redux performance timing error', error));
    }

    return next(action);
  };

/*
 * Helpers.
 */

function annotateAction(action: AppAction) {
  switch (action.type) {
    case DataGroupsActionsEnum.DATA_UPDATED:
      return annotateDataUpdatedAction(action);

    case PanelActionsEnum.SET_PANEL:
      return annotateSetPanelAction(action);

    default:
      return annotateTaskStep(`redux action ${action.type}`);
  }
}

function annotateDataUpdatedAction(action: DataUpdatedAction) {
  const dataGroupSizes = _(action.payload.result)
    .pickBy(isArray)
    .mapValues((array) => array.length)
    .value();
  annotateTaskStep(`redux action ${action.type}`, dataGroupSizes);
}

function annotateSetPanelAction(action: SetPanelAction) {
  annotateTaskStep(`redux action ${action.type}`, {
    type: action.payload.type,
  });
}

async function measureReduxRender(action: AppAction, state: AppState) {
  const hasPerformanceDebugging = getHasPerformanceDebugLogging(state);
  const hasPerformanceMetrics = getHasPerformanceMetricLogging(state);
  if (!hasPerformanceDebugging && !hasPerformanceMetrics) {
    return;
  }

  maybeSetupWindowListener();

  if (!shouldLogRender()) {
    return;
  }

  const startTime = performance.now();
  const animationFramePromise = inAnimationFrameAsync(
    globalWindow,
    (animationFrameTime) => ({
      animationFrameTime,
      endTime: performance.now(),
    }),
    {source: TaskSourcesEnum.LOGGING},
  );
  const nextTaskPromise = delayAsync().then(() => ({nextTaskTime: performance.now()}));
  const [animationFrameTimes, nextTaskTimes] = await Promise.all([animationFramePromise, nextTaskPromise]);
  maybeReportInteraction(
    startTime,
    animationFrameTimes.endTime,
    animationFrameTimes.animationFrameTime,
    nextTaskTimes.nextTaskTime,
    action,
  );
}

function maybeSetupWindowListener() {
  if (isWindowListenerSetup) {
    return;
  }

  isWindowListenerSetup = true;

  fromVisibilityEvent(globalWindow).subscribe(() => {
    lastVisibilityChange = performance.now();
  });

  fromFocusEvent(globalWindow).subscribe(() => {
    lastFocusChange = performance.now();
  });
}

function shouldLogRender() {
  // Maybe log this render in dev.
  if (isDevelopment() && shouldSampleAllEventsInDev) {
    return true;
  }

  return Math.random() < sampleRate;
}

function maybeReportInteraction(
  startTime: number,
  endTime: number,
  animationFrameTime: number,
  nextTaskTime: number,
  action: AppAction,
) {
  if (
    shouldDiscardInteraction(
      startTime,
      lastVisibilityChange,
      lastFocusChange,
      animationFrameTime,
      nextTaskTime,
    )
  ) {
    return;
  }

  // Compute difference from the start time and send to Datadog.
  interactionPerformanceLogger.info('Measured redux action', {
    duration: endTime - startTime,
    action: action.type,
    animation_frame_latency: endTime - animationFrameTime,
    next_task_latency: nextTaskTime - startTime,
    last_visibility_change: endTime - lastVisibilityChange,
    last_focus_change: endTime - lastFocusChange,
    action_metadata: buildActionMetadata(action),
  });
}

/** Generate additional metadata for logging. */
function buildActionMetadata(action: AppAction) {
  if (action.type === PanelActionsEnum.SET_PANEL) {
    // For the set panel action, include the type of panel.
    return {
      panelType: action.payload.type,
      isPopout: Boolean(action.payload.popupAppPanelId),
    };
  }

  return {};
}
